import { useEffect, useRef } from "react";
import { SITE_ORIGIN } from "@/lib/api";

/**
 * Real-time app-level WebSocket bridge.
 *
 * Subscribes to `/api/ws/user` for the currently-authenticated user.
 * Auth piggybacks on the browser cookies (`access_token` /
 * `session_token`) — no manual token plumbing needed because the WS
 * upgrade request carries them automatically on same-origin connects.
 *
 * The backend joins the socket to two rooms:
 *   • `user:<user_id>`  — events targeted at THIS user
 *   • `admin`           — fan-out events for every admin (only joined
 *                          when the resolved user's role === "admin")
 *
 * Usage:
 *   useUserWebSocket({
 *     enabled: !!user,
 *     onEvent: (msg) => {
 *       if (msg.type === "verification.decision") { ... }
 *     },
 *   });
 *
 * The hook handles reconnection with jittered backoff, sends a ping
 * every 25 s to keep the socket alive through Cloudflare, and cleans
 * up on unmount / dep change so we never leak sockets.
 */
export function useUserWebSocket({ enabled = true, onEvent } = {}) {
  const wsRef = useRef(null);
  const backoffRef = useRef(1000);
  const stoppedRef = useRef(false);
  const pingTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return;
    stoppedRef.current = false;

    const connect = () => {
      if (stoppedRef.current) return;
      try {
        const origin = SITE_ORIGIN.replace(/^http/i, "ws");
        const ws = new WebSocket(`${origin}/api/ws/user`);
        wsRef.current = ws;

        ws.onopen = () => {
          backoffRef.current = 1000;
          // Keepalive ping so intermediaries (CF, K8s ingress) don't
          // idle-close the socket.
          pingTimerRef.current = setInterval(() => {
            try { ws.send(JSON.stringify({ type: "ping" })); } catch (_) { /* ignore */ }
          }, 25000);
        };

        ws.onmessage = (evt) => {
          let msg = null;
          try { msg = JSON.parse(evt.data); } catch (_) { return; }
          if (!msg || msg.type === "pong") return;
          try { onEventRef.current && onEventRef.current(msg); } catch (_) { /* ignore */ }
        };

        const scheduleReconnect = () => {
          if (stoppedRef.current) return;
          if (pingTimerRef.current) {
            clearInterval(pingTimerRef.current);
            pingTimerRef.current = null;
          }
          // Jittered exponential backoff, capped at 15s.
          const delay = Math.min(15000, backoffRef.current) + Math.floor(Math.random() * 500);
          backoffRef.current = Math.min(15000, backoffRef.current * 2);
          reconnectTimerRef.current = setTimeout(connect, delay);
        };

        ws.onclose = scheduleReconnect;
        ws.onerror = () => {
          try { ws.close(); } catch (_) { /* ignore */ }
        };
      } catch (_) {
        // If constructing the WebSocket itself throws (rare — invalid
        // URL in some odd browser), just retry.
        reconnectTimerRef.current = setTimeout(connect, 3000);
      }
    };

    connect();

    return () => {
      stoppedRef.current = true;
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_) { /* ignore */ }
        wsRef.current = null;
      }
    };
  }, [enabled]);
}
